// #region Imports
import { startCase } from "lodash";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { 
  useArchiveFeeScheduleTemplateMutation, 
  useDeleteFeeScheduleTemplateMutation, 
  useGetFeeScheduleTemplatesQuery, 
  useUnarchiveFeeScheduleTemplateMutation 
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { 
  FeeScheduleTemplateOrderParameters 
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  FeeScheduleTemplateLoadIssue,
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByQuery,
  getLastSuccessfulFeeScheduleTemplateQueryByInterface,
  isFeeScheduleTemplateLoadIssue,
  resetFeeScheduleTemplatesByQueryState,
  updateFeeScheduleTemplateInterfaceQuery,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleRowActions from "work/fee-schedule-templates/_views/_row-actions/fee-schedule-row-actions";
import FeeScheduleInternalActionsToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-internal-actions-toolbar";
import FeeScheduleToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-toolbar";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
// #endregion

// #region Styled Components
const TagChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
// #endregion

const columnHelper = createMRTColumnHelper<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>();
type DeserializedFeeScheduleTemplateRow = (FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) & { id: Guid };

function deserializeFeeScheduleTemplates(
  feeScheduleTemplateJsonList: Array<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>,
): DeserializedFeeScheduleTemplateRow[] {
  const deserializedData: DeserializedFeeScheduleTemplateRow[] = [];

  for (const feeScheduleTemplateJson of feeScheduleTemplateJsonList) {
    try {
      if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
        deserializedData.push(feeScheduleTemplateJson);
        continue;
      }

      const feeScheduleTemplate = FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
      if (!feeScheduleTemplate?.id) {
        console.error("FeeScheduleTemplate does not have an id", feeScheduleTemplateJson);
        deserializedData.push({ id: Guid.EMPTY, issue: "Error loading FeeScheduleTemplate." });
        continue;
      }

      deserializedData.push(feeScheduleTemplate as FeeScheduleTemplate & { id: Guid });
    } catch (error) {
      const id = "id" in feeScheduleTemplateJson ? (Guid.fromJSON(feeScheduleTemplateJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
      deserializedData.push({ id, issue: "Error loading FeeScheduleTemplate." });
      console.error("Error deserializing FeeScheduleTemplate JSON:", error);
    }
  }

  return deserializedData;
}

export default function FeeSchedulesPage() {
  // #region State
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  // #region Hooks
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();

  // #region Route
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageTab = pageTabParam as PageTab | undefined;
  const effectivePageTab = pageTab ?? PageTab.Templates;

  // #region Permission Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  // #endregion

  // #region Fee Schedule Queries
  const interfaceName = `feeSchedule${startCase(effectivePageTab)}`;
  const feeScheduleTemplatesQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));

  const paginatedFeeScheduleTemplatesJson = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQueryParams));
  const paginatedFeeScheduleTemplates = useMemo(() => {
    if (!paginatedFeeScheduleTemplatesJson) return;
    return deserializeFeeScheduleTemplates(paginatedFeeScheduleTemplatesJson.data);
  }, [paginatedFeeScheduleTemplatesJson]);

  const feeScheduleTemplatesAPIQuery = useGetFeeScheduleTemplatesQuery(
    feeScheduleTemplatesQueryParams ? { query: feeScheduleTemplatesQueryParams, interfaceName } : skipToken,
  );
  const [archiveFeeScheduleTemplate] = useArchiveFeeScheduleTemplateMutation();
  const [unarchiveFeeScheduleTemplate] = useUnarchiveFeeScheduleTemplateMutation();
  const [deleteFeeScheduleTemplate] = useDeleteFeeScheduleTemplateMutation();

  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulFeeScheduleTemplateQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    return deserializeFeeScheduleTemplates(lastSuccessfulQueryResponseJson.data);
  }, [lastSuccessfulQueryResponseJson]);

  const tableTemplates = paginatedFeeScheduleTemplates ?? lastSuccessfulQueryResponse;

  useEffect(() => {
    const filterParameters = FeeScheduleTemplateFilterParameters.fromSearchParams({ searchParams, session });
    const orderParameters = FeeScheduleTemplateOrderParameters.fromSearchParams(searchParams);

    const newQuery = new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (feeScheduleTemplatesQueryParams && newQuery.isEqualTo(feeScheduleTemplatesQueryParams)) return;
    dispatch(
      updateFeeScheduleTemplateInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    pageTab,
    session.user?.legalEntityId.value,
    session,
    searchParams,
    feeScheduleTemplatesQueryParams,
    dispatch,
    interfaceName
  ]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const tagsFilter = columnFilters.find((filter) => filter.id === "tags")?.value as string | undefined;
    if (tagsFilter) {
      newSearchParams.set("tags", tagsFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(FeeScheduleTemplateOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex;
    if (paginationPageIndex !== undefined) newSearchParams.set("page", (paginationPageIndex + 1).toString());

    const paginationPageSize = pagination.pageSize;
    if (paginationPageSize !== undefined) newSearchParams.set("pageSize", paginationPageSize.toString());

    if (newSearchParams.toString() === searchParams.toString()) return;

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, searchParams, setSearchParams]);

  useEffect(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule templates table", { variant: "error" });
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  // #region Functions
  /**
   * Archive or Unarchive the specified fee schedule template
   * @param id fee schedule id to archive / unarchive
   * @param archive if true, archive, else unarchive
   */
  const handleArchiveFeeScheduleTemplate = useCallback(async (id: Guid | undefined, archive: boolean): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const action = archive ? "Archive" : "Unarchive";

    const response = await confirm({
      title: action,
      message: `${action} fee schedule template?`,
      okButtonText: action,
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      if (archive) await archiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      else await unarchiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar(`${action} fee schedule`, { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action} fee schedule. Please try again`, {
        variant: "error",
      });
    }
  }, [confirm, archiveFeeScheduleTemplate, unarchiveFeeScheduleTemplate, dispatch]);

  /**
   * Deletes the specified fee schedule template
   * @param id The fee schedule template id
   */
  const handleDeleteFeeScheduleTemplate = useCallback(async (id: Guid | undefined): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const response = await confirm({
      title: "Delete?",
      message: `Delete fee schedule template?`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar("Deleted fee schedule", { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete fee schedule. Please try again", {
        variant: "error",
      });
    }
  }, [confirm, dispatch, deleteFeeScheduleTemplate]);
  // #endregion

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: NameCell
      }),
      columnHelper.display({
        header: "Tags",
        id: "tags",
        enableSorting: false,
        filterVariant: "multi-select",
        Cell: TagsCell
      }),
      columnHelper.display({
        header: "Created",
        id: "createdDate",
        Cell: CreatedDateCell
      }),
    ],
    [],
  );

  const openFeeSchedule = useCallback(
    (feeSchedule: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
      if (isFeeScheduleTemplateLoadIssue(feeSchedule)) {
        console.warn("Cannot open fee schedule template due to load issue:", feeSchedule.issue);
        return;
      }
      if (!feeSchedule.id) {
        console.error("Cannot open fee schedule template without id");
        return;
      }

      let tab: PageTab = pageTab ?? PageTab.Templates;
      let action: "view" | "edit" = "view";

      if (tab === PageTab.Templates) {
        action = "edit";
      }

      navigate(`/fee-schedules/${tab}/${action}/${feeSchedule.id.value}`);
    },
    [navigate, pageTab]
  );

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> }) => ({
      onClick: () => openFeeSchedule(row.original),
      sx: {
        backgroundColor: isFeeScheduleTemplateLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" : "inherit",
        cursor: isFeeScheduleTemplateLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openFeeSchedule]
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> }) => (
      <FeeScheduleInternalActionsToolbar table={props.table} />
    ), []
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> }) => (
      <FeeScheduleToolbar table={table} activeTab={pageTab ?? PageTab.Templates} />
    ), [pageTab]
  );

  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> }) => {
      const { row } = props;
      if (isFeeScheduleTemplateLoadIssue(row.original)) return null;
      return (
        <FeeScheduleRowActions
          row={row}
          activeTab={effectivePageTab}
          onArchivedChange={handleArchiveFeeScheduleTemplate}
          onDelete={handleDeleteFeeScheduleTemplate}
        />
      );
    },
    [effectivePageTab, handleArchiveFeeScheduleTemplate, handleDeleteFeeScheduleTemplate]
  );

  const getRowId = useCallback((row: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
    if (isFeeScheduleTemplateLoadIssue(row)) return `load-issue-${row.id}`;
    return row.id?.value ?? "";
  }, []);

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  const tableOptions = useMemo<MRT_TableOptions<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>>(() => {
    return {
      columns,
      data: tableTemplates ?? [],
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId,
      enableRowActions: effectivePageTab !== PageTab.Active,
      enableColumnPinning: true,
      enableFacetedValues: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
      },
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      renderRowActions,
      muiTableContainerProps: {
        sx: {
          flex: 1,
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
      muiToolbarAlertBannerProps,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      rowCount: tableTemplates?.length ?? 0,
      state: {
        columnFilters,
        globalFilter,
        isLoading: feeScheduleTemplatesAPIQuery.isFetching,
        pagination,
        showAlertBanner: Boolean(feeScheduleTemplatesAPIQuery.isError),
        showProgressBars: feeScheduleTemplatesAPIQuery.isFetching,
        sorting,
        showSkeletons: feeScheduleTemplatesAPIQuery.isLoading,
      },
    };
  }, [
    columns, 
    tableTemplates, 
    muiTableBodyRowProps, 
    renderToolbarInternalActions, 
    renderTopToolbarCustomActions, 
    getRowId, 
    effectivePageTab, 
    renderRowActions, 
    muiToolbarAlertBannerProps, 
    columnFilters, 
    globalFilter, 
    feeScheduleTemplatesAPIQuery.isFetching, 
    feeScheduleTemplatesAPIQuery.isError, 
    feeScheduleTemplatesAPIQuery.isLoading, 
    pagination, 
    sorting
  ]);

  // #region Render
  if (!sessionPermissions) {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return <MRTNoMemo<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> options={tableOptions} />;
  // #endregion
}

const NameCell = ({ row, table }: {
  row: { original: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue },
  table: MRT_TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) {
    return (
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: "4000px",
              whiteSpace: "normal",
            },
          },
        }}
        title={`Our staff has been notified. Reference id: ${row.original.id.value}`}
      >
        <span>Error Loading Template</span>
      </Tooltip>
    );
  }
  return <RowTitle>{row.original.name}</RowTitle>;
};

const TagsCell = ({ row, table }: {
  row: { original: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue },
  table: MRT_TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) return;
  if (!row.original.tags.length) return <TagChip disabled label="No Tags" size="small" />;
  return (
    <div>
      {row.original.tags.map((tag) => (
        <TagChip key={tag.value as React.Key} label={tag.value} size="small" />
      ))}
    </div>
  );
};

const CreatedDateCell = ({ row, table }: {
  row: { original: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue },
  table: MRT_TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original) || !row.original.created) return;
  return <time>{row.original.created?.asFormattedString}</time>;
};
